import React from "react";
import { motion } from "framer-motion";
import { formatAddress, formatHash, formatDate, formatAmount, formatValue, formatStatusBadge, getCurrencyColor } from "../../utils/tableUtils";
import { handleCopy } from "../../utils/handleCopy";

/**
 * Renders a single cell based on column config and item data using standardized formatting rules (DRY pattern).
 */
export const renderDefaultCell = (item, col, index, globalRowIndex, options = {}) => {
  const { styles = {}, copiedText, setCopiedText } = options;
  const key = col.key;

  // 1. Custom column render function
  if (typeof col.render === "function") {
    return col.render(item, index, globalRowIndex);
  }

  // 2. Built-in cell formatting based on key
  switch (key) {
    case "id":
      return <span style={{ fontWeight: "500", color: "#A3A3A3" }}>{globalRowIndex < 10 ? `0${globalRowIndex}` : globalRowIndex}</span>;

    case "amount": {
      const val = item.amount !== undefined ? item.amount : item.value;
      return formatAmount(val, 3, 3, "#6FE6B8");
    }

    case "value": {
      return formatValue(item.value, 3, 3);
    }

    case "payable":
    case "payableAmount":
    case "payAbleAmount": {
      const val = item.payAbleAmount !== undefined ? item.payAbleAmount : item.payableAmount !== undefined ? item.payableAmount : item.payable;
      return formatAmount(val, 3, 3, "#6FE6B8");
    }

    case "commission":
    case "commision":
    case "adminCommission": {
      const val = item.adminCommission !== undefined ? item.adminCommission : (item.commission !== undefined ? item.commission : (item.commision !== undefined ? item.commision : (item.commissionAmount !== undefined ? item.commissionAmount : (item.admin_commission !== undefined ? item.admin_commission : 0))));
      return formatAmount(val, 3, 3, "#f2f4f3");
    }

    case "charge":
    case "charges":
    case "adminFee":
    case "fee": {
      const val = item.charge !== undefined ? item.charge : (item.charges !== undefined ? item.charges : (item.adminFee !== undefined ? item.adminFee : (item.fee !== undefined ? item.fee : (item.admin_fee !== undefined ? item.admin_fee : 0))));
      return formatAmount(val, 3, 3, "#f2f4f3");
    }

    case "remark":
    case "remarks":
      return <span style={{ color: "#A3A3A3", fontWeight: 500 }}>{item.remarks || item.remark || "-"}</span>;

    case "currency":
    case "tokenName": {
      const token = item.tokenName || item.currency || "USDT";
      return (
        <span className={`${styles.badgeCurrency || ""} ${getCurrencyColor(token, styles)}`}>
          {token}
        </span>
      );
    }

    case "apiKey": {
      const apiVal = item.apiKey;
      if (!apiVal) return <span style={{ color: "#A3A3A3" }}>-</span>;
      return (
        <div className="d-inline-flex align-items-center gap-1">
          <span className={styles.codeLink || styles.apiKeyBadge || ""} title={apiVal}>
            {apiVal}
          </span>
          <button
            type="button"
            className={styles.btnCopy || "btn btn-sm"}
            onClick={(e) => handleCopy(setCopiedText, apiVal, e)}
            title="Copy API Key"
          >
            <i className={`fas ${copiedText === apiVal ? "fa-check text-success" : "fa-copy"}`}></i>
          </button>
        </div>
      );
    }

    case "apiSecret": {
      const secretVal = item.apiSecret;
      return (
        <div className="d-inline-flex align-items-center gap-1">
          <span className={styles.codeSecret || ""} title={secretVal}>
            {formatHash(secretVal, 8, 6)}
          </span>
          {secretVal && (
            <button
              type="button"
              className={styles.btnCopy || "btn btn-sm"}
              onClick={(e) => handleCopy(setCopiedText, secretVal, e)}
              title="Copy API Secret"
            >
              <i className={`fas ${copiedText === secretVal ? "fa-check text-success" : "fa-copy"}`}></i>
            </button>
          )}
        </div>
      );
    }

    case "ip":
    case "ipAddress": {
      const ipVal = item.ipAddress || item.ip;
      return (
        <div className="d-inline-flex align-items-center gap-1">
          <span className={styles.codeLink || ""} title={ipVal}>
            {ipVal}
          </span>
          {ipVal && (
            <button
              type="button"
              className={styles.btnCopy || "btn btn-sm"}
              onClick={(e) => handleCopy(setCopiedText, ipVal, e)}
              title="Copy IP Address"
            >
              <i className={`fas ${copiedText === ipVal ? "fa-check text-success" : "fa-copy"}`}></i>
            </button>
          )}
        </div>
      );
    }

    case "hash":
    case "transactionHash": {
      const hashVal = item.transactionHash || item.hash;
      return (
        <div className="d-inline-flex align-items-center gap-1">
          <a
            href={`https://bscscan.com/tx/${hashVal}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.hashLink || "text-primary"}
            title={hashVal || "Transaction Hash"}
          >
            {formatHash(hashVal)}
          </a>
          {hashVal && (
            <button
              type="button"
              className={styles.btnCopy || "btn btn-sm"}
              onClick={(e) => handleCopy(setCopiedText, hashVal, e)}
              title="Copy hash"
            >
              <i className={`fas ${copiedText === hashVal ? "fa-check text-success" : "fa-copy"}`}></i>
            </button>
          )}
        </div>
      );
    }

    case "address":
    case "walletAddress":
    case "from": {
      const addrVal = item.walletAddress || item.from || item.address;
      return (
        <div className="d-inline-flex align-items-center gap-1">
          <a
            href={`https://bscscan.com/address/${addrVal}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.walletLink || "text-primary"}
            title={addrVal || "Wallet Address"}
          >
            {formatAddress(addrVal)}
          </a>
          {addrVal && (
            <button
              type="button"
              className={styles.btnCopy || "btn btn-sm"}
              onClick={(e) => handleCopy(setCopiedText, addrVal, e)}
              title="Copy address"
            >
              <i className={`fas ${copiedText === addrVal ? "fa-check text-success" : "fa-copy"}`}></i>
            </button>
          )}
        </div>
      );
    }

    case "status":
      return formatStatusBadge(item.status, styles);

    case "date":
    case "createdAt":
      return (
        <span style={{ color: "#A3A3A3", fontWeight: 500 }} title={formatDate(item.createdAt || item.date, "DD MMM YYYY, hh:mm A")}>
          {formatDate(item.createdAt || item.date)}
        </span>
      );

    default: {
      const cellVal = item[key];
      if (cellVal === undefined || cellVal === null || cellVal === "") return "-";
      if (typeof cellVal === "number") {
        return formatValue(cellVal, 2, 3);
      }
      if (typeof cellVal === "string" && !isNaN(cellVal) && cellVal.trim() !== "") {
        return formatValue(cellVal, 2, 3);
      }
      return String(cellVal);
    }
  }
};

/**
 * Reusable default row renderer to eliminate duplicate renderRow definitions across pages (DRY principle).
 */
export const renderDefaultRow = (item, index, globalRowIndex, columns, options = {}) => {
  const { tableRowVariants, levitateHover } = options;

  if (tableRowVariants) {
    return (
      <motion.tr
        key={item.id || item._id || index}
        custom={index}
        variants={tableRowVariants}
        initial="hidden"
        animate="visible"
        {...(levitateHover || {})}
      >
        {columns.map((col, cIdx) => (
          <td key={col.key || cIdx}>
            {renderDefaultCell(item, col, index, globalRowIndex, options)}
          </td>
        ))}
      </motion.tr>
    );
  }

  return (
    <tr key={item.id || item._id || index}>
      {columns.map((col, cIdx) => (
        <td key={col.key || cIdx}>
          {renderDefaultCell(item, col, index, globalRowIndex, options)}
        </td>
      ))}
    </tr>
  );
};

export default renderDefaultRow;
